import { z } from 'zod';
import type { RefinementCtx } from 'zod';
import type { MarkStatus } from 'services/assist-api/entities/test-instance-response-mark/types';

const errorPrefix = 'Please select';

const markItemRequiredMessage = `${errorPrefix} a rubric item option`;

const commentRequiredMessage =
  'Please add a comment explaining the other reason why this mark needs attention';

const markItemSchema = z.object({
  id: z.number().optional(),
  submittedValue: z.string(),
  rubricItemId: z.number(),
  rubricItemOptionId: z.number().optional(),
  responseItemId: z.number(),
  activeMarkerId: z.number().optional()
});

/**
 * Represents a mark item on the form. Most fields are optional so that draft
 * mark items can be set up (but not saved) for validation purposes.
 * When submitting, the schema will be refined to check for that the user has
 * included a submitted value and other optional fields will be added
 * automatically by the application.
 */
export type MarkItemFormValue = z.infer<typeof markItemSchema>;

const attentionReasonSchema = z.object({
  id: z.number(),
  identifier: z.string(),
  checked: z.boolean()
});
export type AttentionReasonFormValue = z.infer<typeof attentionReasonSchema>;

export enum SubmitAction {
  SaveDraftMarking = `saveDraftMarking`,
  SubmitMarking = `submitMarking`
}

const unrefinedMarkResponseFormSchema = z.object({
  markItems: z.array(markItemSchema),
  status: z.custom<MarkStatus>(),
  flagForAttention: z.boolean().optional(),

  // Used to track if the form should recheck if the Confirm Flag for Attention modal should be
  // displayed again on save:
  hasAskedToFlagForAttention: z.boolean(),

  submitForReview: z.boolean(), // Used to track if the form should be submitted for review when flag for attention is set.
  attentionReasons: z.array(attentionReasonSchema),
  comment: z.string().optional(),
  currentUserMarkerId: z.number().optional(), // Optional; admins can browse mark with being a marker.
  activeMarkerId: z.number().optional(), // Optional; admins can browse the form without an active marker being set.
  otherMarkingKnown: z.boolean(), // Used to track the current knowledge of other marking of the response.
  submitAction: z.custom<SubmitAction>()
});
export type MarkResponseFormValues = z.infer<typeof unrefinedMarkResponseFormSchema>;

function setMarkItemIssues(markItems: MarkItemFormValue[], ctx: RefinementCtx): void {
  markItems.forEach((markItem, markItemIndex) => {
    if (markItem.submittedValue === '') {
      const markItemFieldPath = `markItems.${markItemIndex}`;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: markItemRequiredMessage,
        path: [markItemFieldPath]
      });
    }
  });
}

export const submitMarkResponseFormSchema = unrefinedMarkResponseFormSchema
  .superRefine((formData: MarkResponseFormValues, ctx: RefinementCtx) => {
    // Ensure that there is a comment if the "Other (please specify)" attention
    // reason is checked.
    if (
      formData.attentionReasons.find((ar) => ar.identifier === 'other')?.checked &&
      formData.comment === ''
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: commentRequiredMessage,
        path: ['comment']
      });
    }
  })
  .superRefine((formData: MarkResponseFormValues, ctx: RefinementCtx) => {
    // When submitting, if the mark is not flagged for attention, ensure that
    // all items have a mark. We allow unmarked items when flagging for
    // attention to ensure that the mark can be submitted for review and
    // continued by a helper marker.
    if (!formData.flagForAttention) {
      // Add custom issues for each mark item that does not has a
      // submittedValue, removing the custom issues if the mark item now has a
      // value.
      setMarkItemIssues(formData.markItems, ctx);
    }
  });

export const draftMarkResponseFormSchema = unrefinedMarkResponseFormSchema;
